import { addNotification } from '@services/notifications.service';

const STORAGE_KEY = 'sprinto_app_bug_reports';

export const getStoredBugReports = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.map((r) => ({
                    ...r,
                    links: Array.isArray(r.links) ? r.links : [],
                    files: Array.isArray(r.files) ? r.files : [],
                }));
            }
        }
    } catch {
        // ignore storage errors
    }
    return [];
};

export const saveStoredBugReports = (reports) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(reports));
    } catch {
        // ignore storage errors
    }
};

export const fetchBugReports = async () => {
    return getStoredBugReports();
};

export const createBugReport = async (payload, currentUser) => {
    const reports = getStoredBugReports();
    const nextNum = 1001 + reports.length;
    const ticketNumber = `BUG-${nextNum}`;

    const formattedLinks = (payload.links || []).map((l, i) => ({
        id: l.id || `link-${Date.now()}-${i}`,
        url: l.url || l.href,
        title: l.title || l.url,
    }));

    const formattedFiles = (payload.files || []).map((f, i) => ({
        id: f.id || `file-${Date.now()}-${i}`,
        name: f.name || f.title || `attachment_${i + 1}`,
        type: f.type || '',
        preview: f.preview || '',
    }));

    const newReport = {
        id: `bug-${Date.now()}`,
        ticketNumber,
        title: payload.title,
        description: payload.description,
        links: formattedLinks,
        files: formattedFiles,
        status: 'pending',
        createdAt: new Date().toISOString(),
        createdBy: {
            id: currentUser?.id || 'current-user',
            name: currentUser?.name || currentUser?.userName || 'Current User',
            email: currentUser?.email || 'user@codefied.co',
            initials: (currentUser?.name || 'User').split(' ').map((n) => n[0]).join('').toUpperCase(),
        },
    };

    const updated = [newReport, ...reports];
    saveStoredBugReports(updated);

    // Dispatch creation notification to user
    addNotification({
        title: `Ticket #${ticketNumber} Created`,
        message: `Thank you for reporting! Ticket #${ticketNumber} ("${payload.title}") has been received. Our team will resolve it as soon as possible.`,
        type: 'bug_report',
        metadata: { ticketNumber, bugId: newReport.id },
    });

    return newReport;
};

export const updateBugReportStatus = async (id, newStatus, actorUser) => {
    const reports = getStoredBugReports();
    let targetReport = null;

    const updated = reports.map((r) => {
        if (r.id === id || r.ticketNumber === id) {
            targetReport = {
                ...r,
                status: newStatus,
                updatedAt: new Date().toISOString(),
                updatedBy: {
                    id: actorUser?.id,
                    name: actorUser?.name || actorUser?.userName || 'Admin',
                },
            };
            return targetReport;
        }
        return r;
    });

    saveStoredBugReports(updated);

    // Dispatch resolution notification if status changed to done
    if (newStatus === 'done' && targetReport) {
        addNotification({
            title: `Ticket #${targetReport.ticketNumber} Resolved`,
            message: `Your ticket #${targetReport.ticketNumber} ("${targetReport.title}") has been marked as Done. Thank you for your feedback!`,
            type: 'bug_resolved',
            metadata: { ticketNumber: targetReport.ticketNumber, bugId: targetReport.id },
        });
    }

    return targetReport;
};

export const deleteBugReport = async (id) => {
    const reports = getStoredBugReports();
    const filtered = reports.filter((r) => r.id !== id && r.ticketNumber !== id);
    saveStoredBugReports(filtered);
};
